import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { CoreControlDirective } from '../../../utils/core-control.directive';
import { ErrorComponent } from './components/error/error.component';
import { LabelComponent } from './components/label/label.component';

let nextUniqueLabelId = 0;

@Component({
  selector: 'osp-form-field',
  standalone: true,
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormFieldComponent implements AfterContentInit, OnDestroy {
  @ContentChild(LabelComponent) label?: LabelComponent;
  @ContentChild(CoreControlDirective) coreControl?: CoreControlDirective;
  @ContentChild(ErrorComponent) errorComponent?: ErrorComponent;

  private cdr = inject(ChangeDetectorRef);

  $labelContainer = input(true, { alias: 'labelContainer' });
  $errorContainer = input(true, { alias: 'errorContainer' });

  private control: FormControl<unknown> | null = null;
  private controlStateSubs = new Subscription();
  private controlStatePristineSubs = new Subscription();
  private controlRequiredFieldSubs = new Subscription();
  protected $dirty = signal(false);

  protected $isRequired = signal(false);

  constructor() {
    effect(
      () => {
        if (this.coreControl?.control) {
          this.control = this.coreControl.control();
          this.controlStateListener();
          this.controlDirtinessListener();
          this.controlRequiredFieldListener();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngAfterContentInit(): void {
    this.applyId();
  }

  ngOnDestroy(): void {
    this.controlStateSubs.unsubscribe();
    this.controlStatePristineSubs.unsubscribe();
    this.controlRequiredFieldSubs.unsubscribe();
  }

  private controlStateListener(): void {
    this.controlStateSubs.unsubscribe();
    this.controlStateCheck();
    if (this.control) {
      this.controlStateSubs = combineLatest([this.control.valueChanges, this.control.statusChanges]).subscribe(() =>
        this.controlStateCheck(),
      );
    }
  }

  private controlDirtinessListener(): void {
    this.controlStatePristineSubs.unsubscribe();
    if (this.coreControl) {
      this.controlStatePristineSubs = this.coreControl.isPristine$.subscribe(() => {
        this.controlStateCheck();
      });
    }
  }

  private controlRequiredFieldListener(): void {
    this.controlRequiredFieldSubs.unsubscribe();
    this.controlRequiredCheck();

    if (this.coreControl) {
      this.controlRequiredFieldSubs = this.coreControl.isRequired$.subscribe(() => {
        this.controlRequiredCheck();
      });
    }
  }

  private controlStateCheck(): void {
    const needMarkForCheck = this.$dirty() !== this.control?.dirty;
    this.$dirty.set(this.control?.dirty ?? false);

    if (this.errorComponent) {
      this.errorComponent.setControlErrors(this.control?.errors ?? null);
    }

    if (needMarkForCheck) {
      this.cdr.markForCheck();
    }
  }

  private controlRequiredCheck(): void {
    const needMarkForCheck = this.$isRequired() !== this.control?.hasValidator(Validators.required);
    this.$isRequired.set(this.control?.hasValidator(Validators.required) ?? false);

    if (this.label) {
      this.label.setRequired(this.$isRequired());
      if (needMarkForCheck) {
        this.cdr.markForCheck();
      }
    }
  }

  private applyId(): void {
    let inputId = `osp-form-field-${nextUniqueLabelId++}`;

    if (this.coreControl) {
      if (this.coreControl.id) {
        inputId = this.coreControl.id;
      } else {
        this.coreControl.id = inputId;
      }
    }

    if (this.label && !this.label.for) {
      this.label.for = inputId;
    }
  }


}
